import {
  useContext,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { PlayingCardFace } from "@letele/playing-cards/dist/index.esm.js";
import { CARD_BACK, CARD_DECKS, CardDeckContext } from "../game/card-decks";
import { cardFacesReady } from "../game/card-faces";
import { loadedFaceSrc, markDeckFaceBroken } from "../game/deck-assets";
import { useRenderDeck } from "../game/use-card-faces";
import { playSfx } from "../sound/sfx";

/** Unicode suit used only in the (Latin) `aria-label` — kept stable so the
 * engine tests + every table screen keep finding cards by name (`"2♣"`). */
const SUIT_SYMBOL: Record<string, string> = { C: "♣", D: "♦", H: "♥", S: "♠" };

/**
 * Rank chars whose `@letele/playing-cards` suffix differs from our two-char
 * CardId. Digits `2`–`9` map to themselves; `T` (our "ten") becomes `10`.
 */
const RANK_TO_LETELE: Record<string, string> = { A: "a", T: "10", J: "j", Q: "q", K: "k" };

/** Parse a 2-char CardId (`"TD"`, `"QS"`, `"2C"`) → the standard face component
 * plus the stable Latin `aria-label` (`"10♦"`, `"Q♠"`, `"2♣"`).
 *
 * The faces come from `@letele/playing-cards` (Adrian Kennard's classic English-pattern deck,
 * CC0), which is loaded as its own chunk — see `game/card-faces.ts` for why. The package names
 * each component `${Suit}${rank}` (Suit ∈ C,D,H,S; rank ∈ a,2–10,j,q,k), so we translate our own
 * ids on the fly. See `assets/cards/ATTRIBUTION.md`. */
function faceOf(id: string): { label: string; Face: PlayingCardFace | undefined } {
  const rankChar = id.charAt(0);
  const suitChar = id.charAt(1);
  const rank = rankChar === "T" ? "10" : rankChar;
  const suit = SUIT_SYMBOL[suitChar] ?? "";
  const leteleRank = RANK_TO_LETELE[rankChar] ?? rankChar;
  return { label: `${rank}${suit}`, Face: cardFacesReady()?.[`${suitChar}${leteleRank}`] };
}

export interface CardProps {
  id: string;
  onClick?: () => void;
  disabled?: boolean;
  selected?: boolean;
  faceDown?: boolean;
  small?: boolean;
  /** A slightly larger card (50×70) — used by the Hokm table so hand/trick cards
   * read better on a phone. Ignored when `small` is set. */
  large?: boolean;
  /** The deck picker's compact preview box (40×56) — see {@link PREVIEW_CARD_BOX} for why. */
  preview?: boolean;
  /** Position in a freshly-dealt hand → staggered slide-in animation. */
  dealIndex?: number;
  /** A card just played into the trick/centre → throw-in animation (on mount). */
  thrown?: boolean;
  /**
   * The card's entrance runs ELSEWHERE — its table flies the card's own place in (پاسور's deal out of
   * the stock, `game/use-pasur-deal.ts`) — so it has no CSS entrance of its own, and it LOOKS like a
   * card that had one: undimmed while disabled (the operator's rule below, 2026-10-03).
   */
  entered?: boolean;
  /**
   * A per-frame token (see {@link import("../game/use-frame-key").useFrameKey}) marking that THIS
   * tap COMMITS a move to the server. When set, the card sounds + fires `onClick` AT MOST ONCE per
   * distinct `commitKey`: repeat taps while the move is still in flight (no new frame yet) are fully
   * ignored — no duplicate «play» cue, no duplicate send. On a weak connection a move can sit
   * unconfirmed for seconds, so a fixed-time debounce is not enough; the latch holds for the whole
   * frame and releases when the next server frame arrives.
   *
   * OMITTED for SELECTION taps (بلوف toggle, بی‌دل passing) — those are instant, local, and meant to
   * be tapped repeatedly (select / deselect), so they keep firing on every tap.
   */
  commitKey?: string | number;
  /**
   * Suppress the card's own «play» cue — for a tap whose action has a VOICE OF ITS OWN (the same
   * contract as `Button`'s `silent`). «تأیید قبل از بازی کارت» (`game/lift-to-play.ts`): a card tap
   * only LIFTS the card, silently; the ONE «play» cue sounds on the table tap that plays it. The
   * tap still fires `onClick` and still honours `commitKey`.
   */
  silent?: boolean;
  /**
   * «تأیید قبل از بازی کارت»: THIS is the lifted card — its button carries `data-lifted="1"` (the
   * layout probes find it by that). The look is the caller's (`selected` + the fan's rise).
   */
  lifted?: boolean;
  /**
   * A playable card told to stay still — no gold `card-legal-pulse`. «تأیید قبل از بازی کارت»: while
   * one card is lifted, the OTHER playable cards rest (no rise, no pulse), so nothing invites the
   * mis-tap the setting exists to stop. Still tappable: a tap on one switches the lift.
   */
  resting?: boolean;
  /**
   * A playable card whose cue NEVER pulses: the steady frame (`LEGAL_CUE_STEADY`) from the start.
   * پاسور's capture picker: its pool cards are offered only after your own lift, MID-turn, and a cue
   * that appears mid-turn does not pulse (card-12 — see `LEGAL_CUE_SETTLE`).
   */
  steadyCue?: boolean;
  /**
   * The deck picker's SAMPLE of a deck (its `KD`): drawn as soon as THIS face is held, without
   * waiting for the whole deck — the picker shows a deck's look before anyone has picked it. A table
   * never sets it: there a deck is drawn only when all 52 faces are in (`deck-assets.ts`).
   */
  sample?: boolean;
}

/**
 * THE LAST-RESORT READABLE FACE — rank + suit, drawn from nothing but the card's own id.
 *
 * Reached only when an image deck's face has spent every retry AND the classic SVG artwork is not
 * in memory either (it is prefetched for every user after boot, so this is the rare second
 * failure). It exists because the outcome it replaces is the worst one available: a card the
 * player can still tap and play but cannot read. A plain glyph needs no network at all.
 */
function TextFace({ id, w, h }: { id: string; w: number; h: number }): ReactNode {
  const rankChar = id.charAt(0);
  const rank = rankChar === "T" ? "10" : rankChar;
  const suitChar = id.charAt(1);
  const suit = SUIT_SYMBOL[suitChar] ?? "";
  const red = suitChar === "D" || suitChar === "H";
  return (
    <span
      aria-hidden
      data-face="text-fallback"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: w,
        height: h,
        borderRadius: 6,
        background: "#fff",
        color: red ? "#c1121f" : "#111",
        fontWeight: 800,
        lineHeight: 1,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <span style={{ fontSize: Math.round(h * 0.3) }}>{rank}</span>
      <span style={{ fontSize: Math.round(h * 0.34) }}>{suit}</span>
    </span>
  );
}

/**
 * The fill mode of the `deal-card` / `card-throw` entrances: the 0 % frame through the stagger delay
 * (a card waiting its turn stays hidden below), and NOTHING once the card has landed.
 *
 * ⚠️ NEVER `both`/`forwards` — THAT WAS THE «BLURRY CARDS IN TELEGRAM ON WINDOWS» REPORT. Chromium
 * (WebView2) treats a finished animation that keeps filling as an ACTIVE transform + opacity
 * animation, so every dealt hand card sat on its own compositor layer for as long as it was in the
 * hand (CDP LayerTree, seconds after landing: 7/7 hand cards, `ActiveTransformAnimation,
 * ActiveOpacityAnimation`). That layer is rasterised at the right scale, but the compositor then
 * draws it through the fan's rotation and a fractional device-pixel offset — a bilinear resample:
 * Tenengrad −13…−24 %, edges 1.5 → 1.8–2.0 device px, at 125 % / 150 % (and at 2×, where it hides).
 * The final keyframes are `transform: none`-equivalent and `opacity: 1`, so ending the fill changes
 * no pixel at rest — `card-rest-layer.test.tsx`; the instruments are `tools/card-blur-probes/`.
 */
const ENTRANCE_FILL = "backwards";

/**
 * THE MSAA GUARD — one paint without anti-aliasing per classic face, so Chromium's GPU raster draws
 * the card with the same analytic coverage as every CPU raster (and WebKit) instead of MSAA.
 *
 * Telegram Desktop on Windows is WebView2. cc multisamples a layer whose display list holds ≥ 6
 * «slow» paths (an anti-aliased concave path — a letele face is dozens) and NO non-anti-aliased
 * paint (`GetMSAASampleCountForRaster`, Chromium 131): 8× DMSAA on an NVIDIA/AMD GPU (an Intel one
 * is `msaa_is_slow` and gets none). Measured on SwiftShader (4×): حکم's fanned hand on YOUR turn —
 * composited for Overlap beside the status pill's pulse — and every card on the پاسور table came out
 * with beaded fine strokes and stepped edges. حکم's felt was spared only because one header
 * `<button>` paints a UA `outset` border without anti-aliasing — an accident the next `border-0`
 * removes.
 *
 * One non-anti-aliased paint anywhere in a layer makes cc refuse MSAA for the WHOLE layer, so each
 * classic face brings its own: a rect drawn `crispEdges` (Blink records it with AA off) and filled
 * FULLY TRANSPARENT — the paint op is in the display list, which is all cc asks about, and it puts no
 * colour anywhere, in any raster, in any engine. (`fill="none"` would record no op and protect
 * nothing — measured: the two arms then differ again.) It covers the card's whole box, so its paint
 * chunk has exactly the face's bounds: whatever culls or layers the face does the same to the guard.
 * Chromium-only by mechanism; inert elsewhere. Image decks need none (a bitmap is not a path).
 *
 * ⚠️ ONLY BELOW 2× DENSITY (`MSAA_GUARD_BELOW_DPR`): that is where the artefacts are a pixel you can
 * see (Windows at 100–175 %), and where the report came from. At 2× and above — a Retina Mac (WebKit,
 * no MSAA at all), every phone's Android WebView — the card stays exactly as it was: MSAA is the raster
 * path Chromium picked there for speed, and this host cannot measure raster cost.
 * `card-msaa-guard.test.tsx` pins the contract; the painting proof is `tools/card-blur-probes/`
 * (`probe-raster-arms.cjs`: dmsaa vs nomsaa on the same page).
 */
const MSAA_GUARD_BELOW_DPR = 2;

function msaaGuard(w: number, h: number): ReactNode {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      data-msaa-guard=""
      width={w}
      height={h}
      style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none", display: "block" }}
    >
      <rect width={w} height={h} fill="#fff" fillOpacity={0} shapeRendering="crispEdges" />
    </svg>
  );
}

/** Stitch card face-shadow + selected (gold-ring) shadow — copied from the
 * design's `.card-shadow` / `.card-selected` element styles. */
const FACE_SHADOW = "0 4px 6px -1px rgba(0,0,0,.5), 0 2px 4px -1px rgba(0,0,0,.3)";
const SELECTED_SHADOW =
  "0 10px 15px -3px rgba(0,0,0,.6), 0 4px 6px -2px rgba(0,0,0,.4), 0 0 0 2px rgb(var(--c-secondary))";

/**
 * THE LEGAL CUE PULSES ONCE A TURN, THEN HOLDS (card-12, the operator 2026-10-28: «on my turn, my
 * ring and playable cards pulse for about 2 s, then hold steady»). It used to be
 * `card-legal-pulse 1.8s … infinite` — a box-shadow repainted on every frame of every turn, the bulk
 * of what heated the phones. Now a playable card plays `card-legal-pulse-settle` (theme.css): the
 * old rise, frame for frame, then down to the MIDDLE of the old range, held by `forwards`. The stagger
 * behind the deal is unchanged (`dealIndex` × .045 + .45 s), so a 13-card hand is steady by 2.34 s
 * and شلم's 16 by 2.48 s. The fill holds a BOX-SHADOW only — not the transform/opacity fill that put
 * each hand card on its own compositor layer (`ENTRANCE_FILL`) — and once it has ended nothing
 * repaints.
 *
 * ONCE A TURN, NEVER AGAIN WITHIN IT. A card that loses its cue for a moment and gets it back — the
 * lift-to-play rest, a pick and its un-pick — would replay the settle if the animation were simply
 * dropped and declared again (that is how CSS restarts one). So the card remembers (`LegalCue`): it
 * settles on its first playable render after being OUT of play — disabled, or with no `onClick`,
 * which is how every table draws a card between your turns — and a cue that comes back before the
 * card has been out of play again is the steady frame, static (`LEGAL_CUE_STEADY`), no animation.
 * The memory is a ref moved after commit (a layout effect), not a timer: CSS does all the timing.
 * A card the pick limit disables (بی‌دل's pass, شلم's کف) is out of play like any other, so an
 * un-pick that frees it settles it again. While a cue lasts, its declaration is the same string on
 * every render, so a new frame (a new `commitKey`) never restarts it.
 */
const LEGAL_CUE_SETTLE = "card-legal-pulse-settle 1.35s ease-in-out";
/** The settle's held last frame, as a static box-shadow (theme.css's token). */
const LEGAL_CUE_STEADY = "var(--card-legal-pulse-steady)";
/** `idle` out of play · `settling` the cue is up and has been since the card came into play ·
 *  `held` it was interrupted (rest / pick) since — any return shows the steady frame. */
type LegalCue = "idle" | "settling" | "held";

/**
 * The single playing-card component used across every table. The **face** is a
 * standard-deck SVG from `@letele/playing-cards` (see `faceOf`), scaled to fill
 * the card box (or the active image deck's face, once that deck is loaded); the **back** is
 * Back B, one for every deck. Public props are
 * frozen so the other five tables and the game logic keep working unchanged, and
 * the `aria-label` stays the Latin `"<rank><suit>"` (`"2♣"`) the tests + tables
 * query by. The `card` / `card--back` class names are kept as markers.
 */
/**
 * The `large` card box. Exported because the deck picker holds a placeholder in a card's place
 * while the artwork chunk is in flight, and it must be EXACTLY this size or the picker row
 * reflows the moment the real face lands.
 */
export const LARGE_CARD_BOX = { w: 50, h: 70 } as const;
/** The `small` card box. */
export const SMALL_CARD_BOX = { w: 36, h: 50 } as const;
/** The DEFAULT card box. */
export const DEFAULT_CARD_BOX = { w: 44, h: 62 } as const;
/**
 * The deck picker's COMPACT preview — between `small` and the default, and the size is a measured
 * result, not a taste. On the binding start screen (حکم with its سرس/نرس reveal open, 320×568) the
 * card has 492.0 px to fill: at the DEFAULT 44×62 it measured **492.1 — over by 0.1 px** — and at
 * `small` 36×50 it left 11.9 px unused. 40×56 is the largest step that fits, with 5.9 px to spare.
 */
export const PREVIEW_CARD_BOX = { w: 40, h: 56 } as const;

export function Card({
  id,
  onClick,
  disabled,
  selected,
  faceDown,
  small,
  large,
  preview,
  dealIndex,
  thrown,
  entered = false,
  commitKey,
  silent = false,
  lifted = false,
  resting = false,
  steadyCue = false,
  sample = false,
}: CardProps): ReactNode {
  const w = preview ? PREVIEW_CARD_BOX.w : small ? 36 : large ? LARGE_CARD_BOX.w : 44;
  const h = preview ? PREVIEW_CARD_BOX.h : small ? 50 : large ? LARGE_CARD_BOX.h : 62;

  // The frame at which this card last committed (see `commitKey`). A repeat tap at the SAME frame —
  // the move is still in flight — is swallowed whole: no cue, no `onClick`.
  const committedAt = useRef<string | number | undefined>(undefined);

  // The asked DECK (from CardDeckContext — the online table's `settings.deck` or the per-user
  // offline pref) skins the FACE; it is DRAWN only once all of it is loaded (`useRenderDeck`, the
  // rule in `deck-assets.ts`) — until then the card is «استاندارد», never a face not yet in memory.
  // The hook runs here, ABOVE the `faceDown` early return, because hooks may not be conditional.
  const askedDeck = useContext(CardDeckContext);
  const renderDeck = useRenderDeck(askedDeck);
  // The picker's sample may draw its one face early; a table only ever draws a ready deck.
  const sampleSrc = sample ? loadedFaceSrc(askedDeck, id) : null;
  const drawnDeck = sampleSrc ? askedDeck : renderDeck;
  const deck = CARD_DECKS[drawnDeck] ?? CARD_DECKS.standard;
  // The face url for an IMAGE deck — the URL the loader fetched (the one in the memory cache) —
  // or null for the classic SVG deck.
  const faceSrc = deck.faces ? (sampleSrc ?? loadedFaceSrc(drawnDeck, id)) : null;
  // The image face this element has seen PAINTABLE (loaded and decoded). Until then the readable
  // classic face stays UNDER it — see `face` below.
  const [paintedSrc, setPaintedSrc] = useState<string | null>(null);

  // A playable card (tappable, not disabled, not already selected, not told to rest) wears the gold
  // cue — settling once a turn (`LEGAL_CUE_SETTLE`). `live`: in play at all (between turns a table
  // disables its cards). The latch is read here and moved only after the commit, so a render React
  // throws away (or runs twice) cannot move it.
  const live = !!onClick && !disabled;
  const legal = live && !selected && !resting;
  const legalCue = useRef<LegalCue>("idle");
  useLayoutEffect(() => {
    if (!live) legalCue.current = "idle";
    else if (legal) {
      if (legalCue.current === "idle") legalCue.current = "settling";
    } else if (legalCue.current === "settling") legalCue.current = "held";
  });
  const cue: "settle" | "steady" | null = !legal
    ? null
    : steadyCue || legalCue.current === "held"
      ? "steady"
      : "settle";

  if (faceDown) {
    // Face-down back — Back B (the operator's pick, 2026-10-20), ONE back for every deck, inlined
    // into the bundle (`vite.config.ts`), so it is never fetched and never blank. The image IS the
    // back: theme.css draws nothing over it (its old rim/cross-hatch/lozenge layers are gone).
    return (
      <span
        className="card card--back"
        aria-hidden
        style={{
          position: "relative",
          width: w,
          height: h,
          borderRadius: 6,
          overflow: "hidden",
          boxShadow: FACE_SHADOW,
          // A thrown/dealt back honours the same entrance as a face-up card.
          animation: thrown
            ? `card-throw .4s cubic-bezier(.2,.7,.3,1) ${ENTRANCE_FILL}`
            : dealIndex != null
              ? `deal-card .45s cubic-bezier(.2,.7,.3,1) ${(dealIndex * 0.045).toFixed(2)}s ${ENTRANCE_FILL}`
              : undefined,
        }}
      >
        {/* The back — cover-filled into the rounded, clipped box. */}
        <img
          src={CARD_BACK}
          alt=""
          aria-hidden
          width={w}
          height={h}
          style={{
            display: "block",
            width: w,
            height: h,
            objectFit: "cover",
            borderRadius: 6,
          }}
        />
      </span>
    );
  }

  const { label, Face } = faceOf(id);
  // The face drawn below is the classic letele SVG — the standard deck, or an image deck not (yet)
  // loaded — i.e. paths, which is what the MSAA guard is for.
  const classicFace = !faceSrc && !!Face;
  const guarded = classicFace && window.devicePixelRatio < MSAA_GUARD_BELOW_DPR;

  const delay = dealIndex != null ? dealIndex * 0.045 : 0;
  // Entrance: a thrown card (into the trick) flies in; a freshly-dealt hand card
  // deals in from below (Stitch `card-throw` / `deal-card`).
  const entrance = thrown
    ? `card-throw .4s cubic-bezier(.2,.7,.3,1) ${ENTRANCE_FILL}`
    : dealIndex != null
      ? `deal-card .45s cubic-bezier(.2,.7,.3,1) ${delay.toFixed(2)}s ${ENTRANCE_FILL}`
      : "";
  const animation =
    [
      entrance,
      cue === "settle"
        ? `${LEGAL_CUE_SETTLE} ${(dealIndex != null ? delay + 0.45 : 0).toFixed(2)}s forwards`
        : "",
    ]
      .filter(Boolean)
      .join(", ") || undefined;

  const style: CSSProperties = {
    position: "relative",
    width: w,
    height: h,
    padding: 0,
    border: "none",
    display: "block",
    borderRadius: 6,
    overflow: "hidden",
    // The standard-deck SVG is white paper; a matching background fills the tiny
    // rounded-corner sliver the SVG's own corners leave.
    background: "#fff",
    boxShadow: selected ? SELECTED_SHADOW : cue === "steady" ? LEGAL_CUE_STEADY : FACE_SHADOW,
    // A card WITH an entrance renders at full opacity even when disabled: the entrance's old
    // `both` fill held its final `opacity: 1` over this inline value for the card's whole life, so
    // every dealt hand card on all five tables has always looked undimmed. Ending the fill
    // (`ENTRANCE_FILL`) must not change that look, so it is stated here instead of inherited.
    // Operator's decision (2026-10-03): unplayable hand cards stay UNDIMMED — not a bug to fix.
    opacity: disabled && !selected && !entrance && !entered ? 0.5 : 1,
    animation,
    cursor: onClick && !disabled ? "pointer" : "default",
  };

  // The face fills the whole card, `aria-hidden` so the wrapper's `aria-label` is the
  // sole accessible name. An IMAGE deck renders its face image — already fetched and decoded, so it
  // paints in the same frame it mounts; the classic `standard` deck renders the letele SVG.
  // `contain` (not cover) so a corner index is NEVER cropped — the image sits WHOLE on the card's
  // white paper (the box background), no edge cut.
  const classic = Face ? (
    <Face
      aria-hidden
      focusable={false}
      // THE THIN BLACK LINE (2026-10-20): letele draws each face on a white <rect> with a 1-unit
      // black stroke flush with the card's edge (0.2 CSS px). Under the box's clip it surfaces as
      // a dark first row/column on whichever sides the sub-pixel phase favours — top + left at
      // Telegram Desktop's 125 %. theme.css drops that stroke for `[data-face="classic"]`; the
      // box draws the paper and its edge. Proof: `tools/deck-load-probes/edge-line.cjs`.
      data-face="classic"
      width={w}
      height={h}
      style={{ display: "block", width: w, height: h }}
    />
  ) : (
    <TextFace id={id} w={w} h={h} />
  );
  // ⚠️ AN IMAGE FACE NEVER PAINTS A BLANK FRAME, WHATEVER THE CACHE DOES. The deck is drawn only
  // once all of it is fetched, decoded and held (`deck-assets.ts`), so a new `<img>` normally finds
  // its picture in the memory cache and paints at once — but «normally» is a browser's cache
  // policy, not a promise (measured 2026-10-20: with the cache bypassed, every card that switched
  // re-fetched and showed white for ~1 s on a slow link). So until THIS element has its picture
  // loaded AND decoded, the readable face (classic, else rank and suit) stays beneath it; the
  // opaque image covers it the moment it paints, and then it goes.
  const face = faceSrc ? (
    <>
      {paintedSrc === faceSrc ? null : classic}
      <img
        src={faceSrc}
        alt=""
        aria-hidden
        onLoad={(e) => {
          const el = e.currentTarget;
          const done = (): void => setPaintedSrc(faceSrc);
          if (typeof el.decode === "function") el.decode().then(done, done);
          else done();
        }}
        // Not expected — the image is held in memory — but not trusted: the deck stops being drawn
        // and every card goes back to «استاندارد» at once while it re-loads (`deck-assets.ts`); a
        // table stays on it for the rest of that hand — the reload switches nothing back
        // (`hand-deck.ts`).
        onError={() => markDeckFaceBroken(drawnDeck, id)}
        width={w}
        height={h}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          display: "block",
          width: w,
          height: h,
          objectFit: "contain",
          borderRadius: 6,
        }}
      />
    </>
  ) : Face ? (
    classic
  ) : askedDeck !== "standard" ? (
    // An image deck not drawable yet AND no classic artwork to borrow (the gate lets a table in
    // on whichever of the two arrives first) — draw the card's own rank and suit. Never the throw
    // below: that one means a `standard` table mounted without its gate, a wiring bug; this is a
    // network that has not delivered yet, which is weather.
    <TextFace id={id} w={w} h={h} />
  ) : (
    // NEVER a blank card. Before the split this branch rendered `null` — a white rectangle — and
    // with the artwork now in its own chunk that would be the visible symptom of a screen mounted
    // before the faces arrived. EVERY renderer of a face-up card is gated on `useCardFaces` (the
    // five tables via their containers, the deck picker on its own) precisely so this is
    // unreachable; if it is ever reached, fail loudly (the ErrorBoundary catches it, shows a real
    // screen and files a `render-crash` beacon) rather than dealing blank cards to a player
    // mid-hand. `card-faces-gate.test.tsx` + `deck-picker-gate.test.tsx` pin it.
    //
    // ⚠️ THE MESSAGE USED TO SAY «the table was mounted without its asset gate». It cost real time
    // on 2026-08-04: the crash it reported was the DECK PICKER on the Settings screen, and the
    // wording sent the investigation looking at tables first. Name the gate, not a guess at the
    // caller — the component stack already says who mounted it.
    ((): never => {
      throw new Error(
        cardFacesReady() === null
          ? `Card "${id}" rendered before the card faces finished loading — whatever mounted it is missing its useCardFaces gate`
          : `Card "${id}" has no face in the loaded deck`,
      );
    })()
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="card"
        style={style}
        disabled={disabled}
        onClick={() => {
          // A committing card fires ONCE per frame. `committedAt` starts `undefined`, so the first
          // tap of a frame always passes; a repeat at the same `commitKey` (move still in flight)
          // is ignored. Selection cards pass no `commitKey`, so the guard is inert for them.
          if (commitKey !== undefined) {
            if (committedAt.current === commitKey) return;
            committedAt.current = commitKey;
          }
          if (!silent) playSfx("play");
          onClick();
        }}
        aria-label={label}
        // review 6b F2: a screen reader hears the lift (the ring is visual only). Only the lifted
        // card carries it — absent elsewhere, so the setting-OFF hand's markup is unchanged.
        aria-pressed={lifted ? true : undefined}
        data-lifted={lifted ? "1" : undefined}
      >
        {face}
        {guarded ? msaaGuard(w, h) : null}
      </button>
    );
  }
  return (
    <span className="card" style={style} aria-label={label}>
      {face}
      {guarded ? msaaGuard(w, h) : null}
    </span>
  );
}
